import { TypeManagerDecorator } from "../../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
    computed,
    inject,
    QueryList,
    signal,
} from "@angular/core";
import type { IngestionFieldDefinition } from "../data.ingestion.service";
import {
    computePaletteDropInsertIndex,
    readPaletteDragPayloadFromDataTransfer,
    isPaletteDragEvent,
} from "./ingestion.field.palette.const";
import {
    canAddIngestionShowHideRuleForField,
    openNewIngestionShowHideRuleModal,
    type IngestionShowHideRuleTargetAction,
} from "./ingestion.admin.form.rules.util";
import { S25RuleTreeComponent } from "../../s25-rule-tree/s25.rule.tree.component";
import { CacheRepository } from "../../../decorators/cache.decorator";
import { AlertService } from "../../../services/alert.service";
import type { IngestionAdminFormComponent } from "./ingestion.admin.form.component";
import { IngestionAdminFormFieldCardComponent } from "./ingestion.admin.form.field.card.component";

type SuggestedField = { label: string; fieldType: IngestionFieldDefinition["fieldType"] };

@TypeManagerDecorator("s25-ng-ingestion-admin-form-fields-tab")
@Component({
    selector: "s25-ng-ingestion-admin-form-fields-tab",
    template: `
        <div class="fields-tab-panel">
            <!-- Event Creation Suggested Fields -->
            @if (
                editor.ingestionPoint.use?.eventCreation &&
                !dismissedSuggestions().has("eventCreation") &&
                filterSuggestions(editor.EVENT_CREATION_SUGGESTIONS).length
            ) {
                <div class="suggested-fields-panel">
                    <div class="suggested-fields-header">
                        <span class="suggested-fields-title">Suggested fields for Event Creation</span>
                        <div class="suggested-fields-header-actions">
                            <button
                                class="aw-button aw-button--outline"
                                (click)="addAllSuggestions(editor.EVENT_CREATION_SUGGESTIONS)"
                            >
                                + Add All
                            </button>
                            <button
                                class="suggested-fields-dismiss"
                                aria-label="Dismiss suggestions"
                                (click)="dismissSuggestions('eventCreation')"
                            >
                                <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                            </button>
                        </div>
                    </div>
                    <div class="suggested-fields-list">
                        @for (
                            suggestion of filterSuggestions(editor.EVENT_CREATION_SUGGESTIONS);
                            track suggestion.label
                        ) {
                            <button class="suggested-field-chip" (click)="addSuggestedField(suggestion)">
                                {{ suggestion.label }}
                                <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                            </button>
                        }
                    </div>
                </div>
            }

            <!-- Event Edit Suggested Fields -->
            @if (
                editor.ingestionPoint.use?.eventEdit &&
                !dismissedSuggestions().has("eventEdit") &&
                filterSuggestions(editor.EVENT_CREATION_SUGGESTIONS).length
            ) {
                <div class="suggested-fields-panel">
                    <div class="suggested-fields-header">
                        <span class="suggested-fields-title">Suggested fields for Event Edit</span>
                        <div class="suggested-fields-header-actions">
                            <button
                                class="aw-button aw-button--outline"
                                (click)="addAllSuggestions(editor.EVENT_CREATION_SUGGESTIONS)"
                            >
                                + Add All
                            </button>
                            <button
                                class="suggested-fields-dismiss"
                                aria-label="Dismiss suggestions"
                                (click)="dismissSuggestions('eventEdit')"
                            >
                                <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                            </button>
                        </div>
                    </div>
                    <div class="suggested-fields-list">
                        @for (
                            suggestion of filterSuggestions(editor.EVENT_CREATION_SUGGESTIONS);
                            track suggestion.label
                        ) {
                            <button class="suggested-field-chip" (click)="addSuggestedField(suggestion)">
                                {{ suggestion.label }}
                                <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                            </button>
                        }
                    </div>
                </div>
            }

            <!-- Registration Suggested Fields -->
            @if (
                editor.ingestionPoint.use?.registration &&
                !dismissedSuggestions().has("registration") &&
                filterSuggestions(editor.REGISTRATION_SUGGESTIONS).length
            ) {
                <div class="suggested-fields-panel">
                    <div class="suggested-fields-header">
                        <span class="suggested-fields-title">Suggested fields for Registration</span>
                        <div class="suggested-fields-header-actions">
                            <button
                                class="aw-button aw-button--outline"
                                (click)="addAllSuggestions(editor.REGISTRATION_SUGGESTIONS)"
                            >
                                + Add All
                            </button>
                            <button
                                class="suggested-fields-dismiss"
                                aria-label="Dismiss suggestions"
                                (click)="dismissSuggestions('registration')"
                            >
                                <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                            </button>
                        </div>
                    </div>
                    <div class="suggested-fields-list">
                        @for (
                            suggestion of filterSuggestions(editor.REGISTRATION_SUGGESTIONS);
                            track suggestion.label
                        ) {
                            <button class="suggested-field-chip" (click)="addSuggestedField(suggestion)">
                                {{ suggestion.label }}
                                <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                            </button>
                        }
                    </div>
                </div>
            }

            <!-- Event Survey Suggested Fields -->
            @if (
                editor.ingestionPoint.use?.eventSurvey &&
                !dismissedSuggestions().has("eventSurvey") &&
                filterSuggestions(editor.EVENT_SURVEY_SUGGESTIONS).length
            ) {
                <div class="suggested-fields-panel">
                    <div class="suggested-fields-header">
                        <span class="suggested-fields-title">Suggested fields for Event Survey</span>
                        <div class="suggested-fields-header-actions">
                            <button
                                class="aw-button aw-button--outline"
                                (click)="addAllSuggestions(editor.EVENT_SURVEY_SUGGESTIONS)"
                            >
                                + Add All
                            </button>
                            <button
                                class="suggested-fields-dismiss"
                                aria-label="Dismiss suggestions"
                                (click)="dismissSuggestions('eventSurvey')"
                            >
                                <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                            </button>
                        </div>
                    </div>
                    <div class="suggested-fields-list">
                        @for (
                            suggestion of filterSuggestions(editor.EVENT_SURVEY_SUGGESTIONS);
                            track suggestion.label
                        ) {
                            <button class="suggested-field-chip" (click)="addSuggestedField(suggestion)">
                                {{ suggestion.label }}
                                <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                            </button>
                        }
                    </div>
                </div>
            }

            <!-- Org Creation Suggested Fields -->
            @if (
                editor.ingestionPoint.use?.orgCreation &&
                !dismissedSuggestions().has("orgCreation") &&
                filterSuggestions(editor.ORG_CREATION_SUGGESTIONS).length
            ) {
                <div class="suggested-fields-panel">
                    <div class="suggested-fields-header">
                        <span class="suggested-fields-title">Suggested fields for Organization Creation</span>
                        <div class="suggested-fields-header-actions">
                            <button
                                class="aw-button aw-button--outline"
                                (click)="addAllSuggestions(editor.ORG_CREATION_SUGGESTIONS)"
                            >
                                + Add All
                            </button>
                            <button
                                class="suggested-fields-dismiss"
                                aria-label="Dismiss suggestions"
                                (click)="dismissSuggestions('orgCreation')"
                            >
                                <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                            </button>
                        </div>
                    </div>
                    <div class="suggested-fields-list">
                        @for (
                            suggestion of filterSuggestions(editor.ORG_CREATION_SUGGESTIONS);
                            track suggestion.label
                        ) {
                            <button class="suggested-field-chip" (click)="addSuggestedField(suggestion)">
                                {{ suggestion.label }}
                                <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                            </button>
                        }
                    </div>
                </div>
            }

            <!-- Org Edit Suggested Fields -->
            @if (
                editor.ingestionPoint.use?.orgEdit &&
                !dismissedSuggestions().has("orgEdit") &&
                filterSuggestions(editor.ORG_CREATION_SUGGESTIONS).length
            ) {
                <div class="suggested-fields-panel">
                    <div class="suggested-fields-header">
                        <span class="suggested-fields-title">Suggested fields for Organization Edit</span>
                        <div class="suggested-fields-header-actions">
                            <button
                                class="aw-button aw-button--outline"
                                (click)="addAllSuggestions(editor.ORG_CREATION_SUGGESTIONS)"
                            >
                                + Add All
                            </button>
                            <button
                                class="suggested-fields-dismiss"
                                aria-label="Dismiss suggestions"
                                (click)="dismissSuggestions('orgEdit')"
                            >
                                <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                            </button>
                        </div>
                    </div>
                    <div class="suggested-fields-list">
                        @for (
                            suggestion of filterSuggestions(editor.ORG_CREATION_SUGGESTIONS);
                            track suggestion.label
                        ) {
                            <button class="suggested-field-chip" (click)="addSuggestedField(suggestion)">
                                {{ suggestion.label }}
                                <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                            </button>
                        }
                    </div>
                </div>
            }

            <!-- Org Join Suggested Fields -->
            @if (
                editor.ingestionPoint.use?.orgJoin &&
                !dismissedSuggestions().has("orgJoin") &&
                filterSuggestions(editor.ORG_JOIN_SUGGESTIONS).length
            ) {
                <div class="suggested-fields-panel">
                    <div class="suggested-fields-header">
                        <span class="suggested-fields-title">Suggested fields for Join Organization</span>
                        <div class="suggested-fields-header-actions">
                            <button
                                class="aw-button aw-button--outline"
                                (click)="addAllSuggestions(editor.ORG_JOIN_SUGGESTIONS)"
                            >
                                + Add All
                            </button>
                            <button
                                class="suggested-fields-dismiss"
                                aria-label="Dismiss suggestions"
                                (click)="dismissSuggestions('orgJoin')"
                            >
                                <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                            </button>
                        </div>
                    </div>
                    <div class="suggested-fields-list">
                        @for (suggestion of filterSuggestions(editor.ORG_JOIN_SUGGESTIONS); track suggestion.label) {
                            <button class="suggested-field-chip" (click)="addSuggestedField(suggestion)">
                                {{ suggestion.label }}
                                <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                            </button>
                        }
                    </div>
                </div>
            }

            <!-- Form Fields Section -->
            <div class="fields-builder-shell">
                <div class="fields-builder-canvas">
                    <div class="form-fields-section">
                        <div class="collapsible-section-content">
                            <div
                                class="fields-builder-canvas-drop-zone"
                                [class.fields-builder-canvas-drop-zone--active]="paletteDropActive()"
                                (dragover)="onCanvasPaletteDragOver($event)"
                                (dragleave)="onCanvasPaletteDragLeave($event)"
                                (drop)="onCanvasPaletteDrop($event)"
                            >
                                @if (editor.fieldDefinitions.length === 0) {
                                    <div class="fields-builder-empty-hint" role="status">
                                        @if (paletteDropActive()) {
                                            <p class="fields-builder-empty-hint-title">Drop to add field</p>
                                        } @else {
                                            <p class="fields-builder-empty-hint-title">Let's build a form</p>
                                            <p class="fields-builder-empty-hint-text">
                                                Click <strong>Add Field</strong>, then choose a field type, or drag one
                                                onto the canvas.
                                            </p>
                                        }
                                    </div>
                                }
                                <div
                                    s25-ng-dnd-sortable
                                    [items]="editor.fieldDefinitions"
                                    (orderChanged)="editor.onFieldDefinitionsOrderChanged()"
                                >
                                    <div
                                        class="field-insert-indicator"
                                        [class.field-insert-indicator--active]="paletteDropInsertIndex() === 0"
                                        aria-hidden="true"
                                    ></div>

                                    @for (fieldDef of editor.fieldDefinitions; track fieldDef; let idx = $index) {
                                        <s25-ng-ingestion-admin-form-field-card
                                            [editor]="editor"
                                            [fieldsTab]="this"
                                            [fieldDef]="fieldDef"
                                            [idx]="idx"
                                        />
                                        <div
                                            class="field-insert-indicator"
                                            [class.field-insert-indicator--active]="
                                                paletteDropInsertIndex() === idx + 1
                                            "
                                            aria-hidden="true"
                                        ></div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section class="fields-builder-rules-section">
                <h3 class="fields-builder-rules-heading">Rules</h3>
                <p class="fields-builder-rules-help">
                    Show/hide field rules for this form. Use <strong>Add Rule</strong> on a field card to pre-select
                    that field. Validation rules are managed on the Rules tab.
                </p>
                <s25-ng-rule-tree
                    #fieldsTabRuleTree
                    [category]="'ingestion'"
                    [subCategory]="editor.ingestionPointId?.toString() || ''"
                    [conditionFilterMap]="{ ingestionFieldsAll: editor.ingestionPointId?.toString() || '' }"
                ></s25-ng-rule-tree>
            </section>
        </div>

        <nav class="field-nav" aria-label="Form fields navigation">
            <h4 class="field-nav-title">Fields</h4>
            <ul>
                @for (fieldDef of editor.fieldDefinitions; track fieldDef; let idx = $index) {
                    @if (!fieldDef.isDeleted) {
                        <li>
                            <a
                                role="button"
                                tabindex="0"
                                (click)="scrollToField(idx)"
                                (keydown.enter)="scrollToField(idx)"
                                >{{ fieldDef.fieldLabel || "Untitled Field" }}</a
                            >
                        </li>
                    }
                }
            </ul>
        </nav>

        @if (fieldsPaletteOpen()) {
            <button
                type="button"
                class="fields-palette-backdrop"
                (click)="closeFieldsPalette()"
                aria-label="Close field palette"
            ></button>
        }
        <div
            class="fields-palette-drawer"
            [class.fields-palette-drawer--open]="fieldsPaletteOpen()"
            [attr.aria-hidden]="!fieldsPaletteOpen()"
        >
            @if (fieldsPaletteOpen()) {
                <div class="fields-palette-drawer-header">
                    <h2 class="fields-palette-drawer-title">Fields</h2>
                    <button
                        type="button"
                        class="fields-palette-drawer-close"
                        (click)="closeFieldsPalette()"
                        aria-label="Close field palette"
                    >
                        <s25-ng-icon [type]="'close'" [ariaLabel]="false" [size]="14" />
                    </button>
                </div>
                <div class="fields-palette-drawer-content">
                    <s25-ng-ingestion-form-fields-palette
                        [ingestionPoint]="editor.ingestionPoint"
                        (tileClick)="editor.addFieldFromType($event.fieldType, undefined, $event.label)"
                        (paletteDragStart)="onPaletteTileDragStart()"
                        (paletteDragEnd)="onPaletteTileDragEnd()"
                    />
                </div>
            }
        </div>

        <div class="fields-builder-floating-actions" role="toolbar" aria-label="Form fields actions">
            <button
                type="button"
                class="aw-button aw-button--outline"
                [class.fields-builder-floating-actions-btn--active]="fieldsPaletteOpen()"
                [attr.aria-expanded]="fieldsPaletteOpen()"
                aria-label="Add field"
                (click)="toggleFieldsPalette()"
            >
                Add Field
            </button>
            @if (editor.fieldDefinitions.length >= 1) {
                <button type="button" class="aw-button aw-button--outline" (click)="toggleAllFields()">
                    {{ allFieldsCollapsed() ? "Expand All" : "Collapse All" }}
                </button>
            }
        </div>
    `,
    styles: `
        :host {
            display: flex;
            gap: 1.5rem;
            align-items: flex-start;
            min-width: 0;
            transition: padding-right 0.2s ease;
        }

        :host.fields-palette-gutter-open {
            padding-right: 280px;
        }

        .fields-tab-panel {
            flex: 1;
            min-width: 0;
        }

        .form-fields-section {
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .collapsible-section-content {
            padding: 20px;
            background: var(--color--surface-bg);
        }

        .suggested-fields-panel {
            border: 1px solid var(--color--border, #ccc);
            border-radius: 0.25rem;
            background: var(--color--page-bg, #f9f9f9);
            padding: 0.75rem 1rem;
            margin-bottom: 0.75rem;
        }

        .suggested-fields-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 0.5rem;
        }

        .suggested-fields-title {
            font-weight: 600;
            font-size: 0.9rem;
        }

        .suggested-fields-header-actions {
            display: flex;
            align-items: center;
            gap: 0.35rem;
        }

        .suggested-fields-dismiss {
            background: none;
            border: none;
            cursor: pointer;
            color: var(--color--text-muted, #666);
            padding: 0.25rem;
            display: flex;
            align-items: center;
        }

        .suggested-fields-dismiss:hover {
            color: var(--color--text, rgba(0, 0, 0, 0.8));
        }

        .suggested-fields-list {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
        }

        .suggested-field-chip {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            background: var(--color--page-bg, #fff);
            border: 1px solid var(--color--border, #ccc);
            border-radius: 0.25rem;
            padding: 0.3rem 0.6rem;
            font-size: 0.85rem;
            cursor: pointer;
        }

        .suggested-field-chip:hover {
            border-color: var(--cn-blue);
            color: var(--cn-blue);
        }

        .fields-builder-shell {
            position: relative;
        }

        .fields-builder-rules-section {
            margin-top: 24px;
            padding: 16px;
            border: 1px solid var(--color--border);
            border-radius: 8px;
            background: var(--color--page-bg);
        }

        .fields-builder-rules-heading {
            margin: 0 0 8px;
            font-size: 1rem;
            font-weight: 600;
            color: var(--color--text-muted);
        }

        .fields-builder-rules-help {
            margin: 0 0 16px;
            font-size: 0.9rem;
            color: var(--color--text-muted);
        }

        .fields-builder-floating-actions {
            position: fixed;
            bottom: calc(1em + 4.25rem);
            right: 4%;
            left: auto;
            z-index: 1000;
            background: var(--color--page-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 12px 20px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            display: flex;
            gap: 8px;
            align-items: center;
            transition: right 0.2s ease;
        }

        :host.fields-palette-gutter-open .fields-builder-floating-actions {
            right: max(1rem, calc(280px + 4%));
            z-index: 1002;
        }

        .fields-builder-floating-actions-btn--active {
            border-color: var(--color--link-text);
            background: var(--color--primary-flat-active-bg);
        }

        @media (max-width: 768px) {
            .fields-builder-floating-actions {
                left: 1rem;
                right: 1rem;
                flex-wrap: wrap;
                justify-content: center;
            }

            :host.fields-palette-gutter-open .fields-builder-floating-actions {
                left: 1rem;
                right: 1rem;
                display: none;
            }

            .fields-builder-empty-hint {
                padding: 32px 16px;
            }
        }

        .fields-builder-empty-hint {
            text-align: center;
            padding: 48px 24px;
            margin-bottom: 16px;
            border: 1px dashed var(--color--border);
            border-radius: 8px;
            background: var(--color--page-bg);
        }

        .fields-builder-empty-hint-title {
            margin: 0 0 8px;
            font-size: 1.1rem;
            font-weight: 600;
            color: var(--color--text-muted);
        }

        .fields-builder-empty-hint-text {
            margin: 0;
            font-size: 0.9rem;
            color: var(--color--text-muted);
        }

        .fields-palette-backdrop {
            display: none;
        }

        .fields-palette-drawer {
            position: fixed;
            top: 0;
            right: 0;
            height: 100vh;
            width: 280px;
            z-index: 1001;
            transform: translateX(100%);
            transition: transform 0.2s ease;
            box-shadow: -4px 0 16px rgba(0, 0, 0, 0.12);
            background: var(--color--page-bg);
            border-left: 1px solid var(--color--border);
            display: flex;
            flex-direction: column;
            pointer-events: none;
        }

        .fields-palette-drawer--open {
            transform: translateX(0);
            pointer-events: auto;
        }

        .fields-palette-drawer-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 1rem;
            border-bottom: 1px solid var(--color--border);
            flex-shrink: 0;
        }

        .fields-palette-drawer-title {
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
        }

        .fields-palette-drawer-close {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            padding: 0;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background: var(--color--page-bg);
            cursor: pointer;
        }

        .fields-palette-drawer-close:hover,
        .fields-palette-drawer-close:focus-visible {
            border-color: var(--color--link-text);
            outline: none;
        }

        .fields-palette-drawer-content {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            padding: 1rem;
        }

        .fields-builder-canvas {
            width: 100%;
            min-width: 0;
            overflow: visible;
        }

        .fields-builder-canvas-drop-zone {
            min-height: 48px;
        }

        .fields-builder-canvas-drop-zone--active {
            outline: 1px dashed var(--color--border);
            outline-offset: 4px;
            border-radius: 8px;
        }

        .field-insert-indicator {
            height: 3px;
            margin: 2px 0;
            border-radius: 2px;
            background: var(--color--primary, #2563eb);
            opacity: 0;
            transition: opacity 0.15s ease;
        }

        .field-insert-indicator--active {
            opacity: 1;
            height: 4px;
            margin: 6px 0;
        }

        .field-nav {
            width: 180px;
            flex-shrink: 0;
            position: sticky;
            top: 1rem;
            max-height: calc(100vh - 5rem);
            overflow-y: auto;
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 12px;
            background: var(--color--page-bg);
        }

        .field-nav-title {
            margin: 0 0 8px;
            font-size: 0.85rem;
            font-weight: 600;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .field-nav ul {
            list-style: none;
            margin: 0;
            padding: 0;
        }

        .field-nav li a {
            display: block;
            padding: 4px 6px;
            border-radius: 4px;
            font-size: 0.85rem;
            cursor: pointer;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            color: var(--color--text);
        }

        .field-nav li a:hover,
        .field-nav li a:focus {
            background: var(--color--surface-2-bg);
            outline: none;
        }

        @media (max-width: 768px) {
            :host.fields-palette-gutter-open {
                padding-right: 0;
            }

            .fields-palette-drawer {
                width: min(100vw, 320px);
            }

            .fields-palette-backdrop {
                display: block;
                position: fixed;
                inset: 0;
                z-index: 1000;
                border: none;
                padding: 0;
                margin: 0;
                background: rgba(0, 0, 0, 0.35);
                cursor: default;
            }
        }

        @media screen and (max-width: 900px) {
            :host {
                display: block;
            }

            .field-nav {
                display: none;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionAdminFormFieldsTabComponent implements OnDestroy {
    @Input({ required: true }) editor!: IngestionAdminFormComponent;

    @ViewChild("fieldsTabRuleTree")
    private fieldsTabRuleTree: S25RuleTreeComponent | undefined;

    @ViewChildren(IngestionAdminFormFieldCardComponent)
    private fieldCards!: QueryList<IngestionAdminFormFieldCardComponent>;

    /** Uploads pending section header images before field definitions are persisted. */
    async flushPendingSectionHeaderImages(): Promise<string | undefined> {
        for (const fieldCard of this.fieldCards) {
            const errorMessage = await fieldCard.flushPendingSectionHeaderImage();
            if (errorMessage) {
                return errorMessage;
            }
        }
        return undefined;
    }

    readonly dismissedSuggestions = signal<ReadonlySet<string>>(new Set());
    readonly paletteDropInsertIndex = signal<number | null>(null);
    readonly paletteDropActive = signal(false);
    readonly fieldsPaletteOpen = signal(false);

    readonly collapsedFields = signal<Set<IngestionFieldDefinition>>(new Set());
    readonly allFieldsCollapsed = computed(
        () =>
            this.collapsedFields().size === this.editor.fieldDefinitions.length &&
            this.editor.fieldDefinitions.length > 0,
    );

    @HostBinding("class.fields-palette-gutter-open")
    get fieldsPaletteGutterOpenClass(): boolean {
        return this.fieldsPaletteOpen();
    }

    private readonly elementRef = inject(ElementRef);
    private readonly cd = inject(ChangeDetectorRef);
    private paletteDocumentDragActive = false;

    private readonly onDocumentPaletteDragOverBound = (event: DragEvent) => this.onDocumentPaletteDragOver(event);
    private readonly onDocumentPaletteDropBound = (event: DragEvent) => this.onDocumentPaletteDrop(event);

    resetFieldsTabState(): void {
        this.collapsedFields.set(new Set());
        this.dismissedSuggestions.set(new Set());
        this.closeFieldsPalette();
        this.clearPaletteDropState();
    }

    toggleFieldsPalette = (): void => {
        this.fieldsPaletteOpen.update((open) => {
            const opening = !open;
            if (opening && this.editor.fieldDefinitions.length > 0) {
                this.collapsedFields.set(new Set(this.editor.fieldDefinitions));
            }
            return opening;
        });
        this.cd.detectChanges();
    };

    closeFieldsPalette = (): void => {
        if (!this.fieldsPaletteOpen()) {
            return;
        }
        this.fieldsPaletteOpen.set(false);
        this.cd.detectChanges();
    };

    @HostListener("document:keydown.escape")
    onEscapeKey(): void {
        if (this.fieldsPaletteOpen()) {
            this.closeFieldsPalette();
        }
    }

    toggleFieldConfig = (fieldDef: IngestionFieldDefinition) => {
        this.collapsedFields.update((set) => {
            const next = new Set(set);
            next.has(fieldDef) ? next.delete(fieldDef) : next.add(fieldDef);
            return next;
        });
        this.cd.markForCheck();
    };

    isFieldExpanded = (fieldDef: IngestionFieldDefinition): boolean => {
        return !this.collapsedFields().has(fieldDef);
    };

    toggleAllFields = () => {
        if (this.allFieldsCollapsed()) {
            this.collapsedFields.set(new Set());
        } else {
            this.collapsedFields.set(new Set(this.editor.fieldDefinitions));
        }
        this.cd.markForCheck();
    };

    notifyFieldDefinitionsChanged = (): void => {
        this.cd.markForCheck();
    };

    scrollToField = (idx: number) => {
        const el = this.elementRef.nativeElement.querySelector(`[data-field-idx="${idx}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    dismissSuggestions = (use: string) => {
        this.dismissedSuggestions.update((suggestions) => new Set([...suggestions, use]));
    };

    filterSuggestions = (suggestions: SuggestedField[]): SuggestedField[] => {
        const active = this.editor.fieldDefinitions.filter((fieldDef) => !fieldDef.isDeleted);
        return suggestions.filter((suggestion) => {
            if (suggestion.fieldType === "TEXT") {
                return !active.some(
                    (fieldDef) => fieldDef.fieldType === "TEXT" && fieldDef.fieldLabel === suggestion.label,
                );
            }
            return !active.some((fieldDef) => fieldDef.fieldType === suggestion.fieldType);
        });
    };

    addAllSuggestions = (suggestions: SuggestedField[]) => {
        for (const suggestion of this.filterSuggestions(suggestions)) {
            this.addSuggestedField(suggestion);
        }
    };

    addSuggestedField = (suggestion: SuggestedField) => {
        this.editor.addFieldFromType(suggestion.fieldType, undefined, suggestion.label);
    };

    refreshIngestionRules = async (): Promise<void> => {
        CacheRepository.invalidateByStartsWith("RuleTreeService.getRulesByPrefix");
        CacheRepository.invalidateByStartsWith("RuleTreeService.getRulesBySubCategory");
        await this.fieldsTabRuleTree?.tableComponent?.refresh(true);
        this.cd.markForCheck();
    };

    onAddRuleForField = (
        fieldDef: IngestionFieldDefinition,
        targetAction: IngestionShowHideRuleTargetAction,
        event: Event,
    ): void => {
        event.stopPropagation();
        if (!canAddIngestionShowHideRuleForField(fieldDef)) {
            AlertService.alert("Save the form before adding rules for this field.");
            return;
        }

        openNewIngestionShowHideRuleModal({
            ingestionPointId: this.editor.ingestionPointId,
            fieldDef,
            targetAction,
            refresh: this.refreshIngestionRules,
        });
    };

    onCanvasPaletteDragOver = (event: DragEvent) => {
        if (this.paletteDocumentDragActive) {
            return;
        }

        if (!isPaletteDragEvent(event)) {
            return;
        }

        event.preventDefault();
        event.dataTransfer!.dropEffect = "copy";
        this.paletteDropActive.set(true);
        this.paletteDropInsertIndex.set(this.getPaletteDropInsertIndex(event.clientY));
    };

    onCanvasPaletteDragLeave = (event: DragEvent) => {
        if (this.paletteDocumentDragActive) {
            return;
        }

        const dropZone = event.currentTarget as HTMLElement | null;
        const relatedTarget = event.relatedTarget as Node | null;
        if (dropZone && relatedTarget && dropZone.contains(relatedTarget)) {
            return;
        }
        this.clearPaletteDropState();
    };

    onCanvasPaletteDrop = (event: DragEvent) => {
        if (this.paletteDocumentDragActive) {
            return;
        }

        if (!isPaletteDragEvent(event)) {
            return;
        }

        event.preventDefault();
        const payload = readPaletteDragPayloadFromDataTransfer(event.dataTransfer);
        const insertIndex = this.paletteDropInsertIndex() ?? this.editor.fieldDefinitions.length;
        this.clearPaletteDropState();

        if (!payload) {
            return;
        }

        this.editor.addFieldFromType(payload.fieldType, insertIndex, payload.label);
    };

    onPaletteTileDragStart = (): void => {
        if (this.paletteDocumentDragActive) {
            return;
        }

        this.paletteDocumentDragActive = true;
        document.addEventListener("dragover", this.onDocumentPaletteDragOverBound, true);
        document.addEventListener("drop", this.onDocumentPaletteDropBound, true);
        this.cd.detectChanges();
    };

    onPaletteTileDragEnd = (): void => {
        this.detachPaletteDocumentDragListeners();
        this.clearPaletteDropState();
        this.cd.detectChanges();
    };

    ngOnDestroy(): void {
        this.detachPaletteDocumentDragListeners();
        this.clearPaletteDropState();
    }

    private onDocumentPaletteDragOver(event: DragEvent): void {
        if (!this.paletteDocumentDragActive && !isPaletteDragEvent(event)) {
            return;
        }

        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
        this.paletteDropActive.set(true);
        this.paletteDropInsertIndex.set(this.getPaletteDropInsertIndex(event.clientY));
        this.cd.detectChanges();
    }

    private onDocumentPaletteDrop(event: DragEvent): void {
        if (!this.paletteDocumentDragActive && !isPaletteDragEvent(event)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const payload = readPaletteDragPayloadFromDataTransfer(event.dataTransfer);
        const insertIndex = this.paletteDropInsertIndex() ?? this.getPaletteDropInsertIndex(event.clientY);

        this.detachPaletteDocumentDragListeners();
        this.clearPaletteDropState();

        if (!payload) {
            this.cd.detectChanges();
            return;
        }

        this.editor.addFieldFromType(payload.fieldType, insertIndex, payload.label);
        this.cd.detectChanges();
    }

    private detachPaletteDocumentDragListeners(): void {
        if (!this.paletteDocumentDragActive) {
            return;
        }

        this.paletteDocumentDragActive = false;
        document.removeEventListener("dragover", this.onDocumentPaletteDragOverBound, true);
        document.removeEventListener("drop", this.onDocumentPaletteDropBound, true);
    }

    private clearPaletteDropState(): void {
        this.paletteDropActive.set(false);
        this.paletteDropInsertIndex.set(null);
    }

    private getPaletteDropInsertIndex(clientY: number): number {
        const cardElements = Array.from(
            this.elementRef.nativeElement.querySelectorAll(
                ".fields-builder-canvas-drop-zone .field-card",
            ) as NodeListOf<HTMLElement>,
        );
        return computePaletteDropInsertIndex(clientY, cardElements);
    }
}
